import { AssetTypeCode, RawAssetTypeCode, WEATHER_RAW_ASSET_TYPES_EXCLUDING_RAIN } from 'types/models/asset-type';
import { SampleDate } from 'types/models/sample';
import SamplePoint, {
  MergedWeatherStationSamplePoint,
  RawSamplePoint,
  SamplePointId,
  SensorTagId,
  WeatherChildSensor
} from 'types/models/samplePoint';

import { type HiddenKey, getHiddenKey } from './hidden-key-to-weather-asset-type';
import parseRawSamplePoint from './parse-raw-samplepoint';

/**
 * Returns true if a weather station controller and a weather station sample point (rawSamplePoint) are associated.
 */
const isMatchingWeatherStationControllerAndSamplePoint = (
  rawSamplePoint: RawSamplePoint,
  accumulatedSP: SamplePoint
) => {
  const isWeatherStationController = accumulatedSP.assetTypeId === AssetTypeCode.WEATHER_STATION_CONTROLLER;

  const isWeatherStationNonRain = WEATHER_RAW_ASSET_TYPES_EXCLUDING_RAIN.includes(rawSamplePoint.assetTypeId);
  const isWeatherStationRainGauge = rawSamplePoint.sensorTags.id === SensorTagId.DAVIS_WEATHER_STATION_RAIN_AU
    || rawSamplePoint.sensorTags.id === SensorTagId.DAVIS_WEATHER_STATION_RAIN_US
    || rawSamplePoint.sensorTags.id === SensorTagId.SPECTRUM_WEATHER_STATION_RAIN;

  const isWeatherStation = isWeatherStationNonRain || isWeatherStationRainGauge;

  const isOnSameMonitor = rawSamplePoint.deviceId === accumulatedSP.deviceId;

  return isWeatherStationController && isWeatherStation && isOnSameMonitor;
};

const pairTemperatureAndDewPoint = (
  controller: MergedWeatherStationSamplePoint,
  currentSP: WeatherChildSensor
) => {
  if (!controller._hidden) return; // No-op

  let currentKey: 'temperature' | 'dewPoint' | undefined;
  let associatedKey: 'temperature' | 'dewPoint' | undefined;
  let associatedSP: WeatherChildSensor | undefined;
  if (currentSP.assetTypeId === AssetTypeCode.TEMPERATURE) {
    const associatedDewPointSP = controller._hidden?.dewPoint;
    if (associatedDewPointSP) {
      currentKey = 'temperature';
      associatedKey = 'dewPoint';
      associatedSP = associatedDewPointSP;
    }
  } else if (currentSP.assetTypeId === AssetTypeCode.DEW_POINT) {
    const associatedTemperatureSP = controller._hidden?.temperature;
    if (associatedTemperatureSP) {
      currentKey = 'dewPoint';
      associatedKey = 'temperature';
      associatedSP = associatedTemperatureSP;
    }
  }

  if (!currentKey || !associatedKey || !associatedSP) return; // No-op

  controller._hidden[currentKey] = {
    ...currentSP,
    _hidden: {
      ...currentSP._hidden!,
      pairedSamplePointId: associatedSP.id
    }
  };
  controller._hidden[associatedKey] = {
    ...associatedSP,
    _hidden: {
      ...associatedSP._hidden!,
      pairedSamplePointId: currentSP.id
    }
  };
};

const pairSoilMoistureAndSoilTemperature = (
  controller: MergedWeatherStationSamplePoint,
  currentSP: WeatherChildSensor
) => {
  if (!controller._hidden) return; // No-op

  let currentKey: 'soilMoisture' | 'soilTemperature' | undefined;
  let associatedKey: 'soilMoisture' | 'soilTemperature' | undefined;
  let associatedSP: WeatherChildSensor | undefined;
  if (currentSP.assetTypeId === AssetTypeCode.SOIL) {
    const associatedSoilTemperatureSamplePoint = controller._hidden?.soilTemperature;
    if (associatedSoilTemperatureSamplePoint) {
      currentKey = 'soilMoisture';
      associatedKey = 'soilTemperature';
      associatedSP = associatedSoilTemperatureSamplePoint;
    }
  } else if (currentSP.assetTypeId === AssetTypeCode.SOIL_TEMPERATURE) {
    const associatedSoilMoistureSamplePoint = controller._hidden?.soilMoisture;
    if (associatedSoilMoistureSamplePoint) {
      currentKey = 'soilTemperature';
      associatedKey = 'soilMoisture';
      associatedSP = associatedSoilMoistureSamplePoint;
    }
  }

  if (!currentKey || !associatedKey || !associatedSP) return; // No-op

  controller._hidden[currentKey] = {
    ...currentSP,
    _hidden: {
      ...currentSP._hidden!,
      pairedSamplePointId: associatedSP.id
    }
  };
  controller._hidden[associatedKey] = {
    ...associatedSP,
    _hidden: {
      ...associatedSP._hidden!,
      pairedSamplePointId: currentSP.id
    }
  };
};

/**
 * Merge incoming raw sample point into weather station controller (accumulatedSP).
 * Should handle multiple cases for adding context via _hidden property.
 * Case 1: WS associated sensors should have a reference to the controller.
 * Case 1: WS Controller should have a reference to all its associated sensors (but not itself).
 * Case 3.1: Temperature sensor should have a reference to its paired Dew Point sensor.
 * Case 3.2: Dew Point sensor should have a reference to its paired Temperature sensor.
 * Case 3.3: Soil Moisture should have a reference to its paired Soil Temperature sensor.
 * Case 3.4: Soil Temperature should have a reference to its paired Soil Moisture sensor.
 */
const mergeWeatherStationSamplePoints = (
  rawWSSamplePoint: RawSamplePoint,
  accumulatedSP: MergedWeatherStationSamplePoint
): MergedWeatherStationSamplePoint => {
  const parsedSP: SamplePoint | null = parseRawSamplePoint(rawWSSamplePoint);
  if (!parsedSP) return accumulatedSP;

  /**
   * Soil Moisture and Soil Temperature share `AssetTypeCode.SOIL` after going through `parseRawSamplePoint`.
   * To display them as two distinct cards on the WeatherStation dashboard, the child copy stored under the controller
   * gets a more specific `AssetTypeCode.SOIL` and `AssetTypeCode.SOIL_TEMPERATURE` derived from
   * RawAssetTypeCode.SOIL_MOISTURE and RawAssetTypeCode.SOIL_TEMP.
   */
  const childAssetTypeId = (() => {
    switch (rawWSSamplePoint.assetTypeId) {
      case RawAssetTypeCode.SOIL_MOISTURE: return AssetTypeCode.SOIL;
      case RawAssetTypeCode.SOIL_TEMP: return AssetTypeCode.SOIL_TEMPERATURE;
      default: return parsedSP.assetTypeId;
    }
  })();

  const weatherStationAssetKey: HiddenKey | undefined = getHiddenKey(childAssetTypeId);
  if (!weatherStationAssetKey) return accumulatedSP;

  const controller: MergedWeatherStationSamplePoint = accumulatedSP;

  // Multiple sensors of the same type may exist in the same device,
  // usually a case where a sensor is replaced by another sensor or a data issue.
  // We must be careful not to overwrite the newest one.
  const incomingSPLastSampleDate: SampleDate = parsedSP.lastSampleDate ?? 0;
  const existingSPLastSampleDate: SampleDate = controller._hidden?.[weatherStationAssetKey]?.lastSampleDate ?? 0;
  if (existingSPLastSampleDate >= incomingSPLastSampleDate) {
    return controller;
  }

  const childSensor: WeatherChildSensor = {
    ...parsedSP,
    assetTypeId: childAssetTypeId,
    // Case 1
    _hidden: { controllerSid: controller.sid } // Allow the controller to be referenced by the child sensor
  };
  controller._hidden = {
    ...controller._hidden, // Copy the existing associated sample points
    // Case 2
    [weatherStationAssetKey]: childSensor
  };

  // Cases 3.1 and 3.2
  pairTemperatureAndDewPoint(controller, childSensor);

  // Cases 3.3 and 3.4
  pairSoilMoistureAndSoilTemperature(controller, childSensor);

  return controller;
};

/**
 * It takes an array of parsed samplePoints and a raw samplePoint, and returns an array of
 * (merged) parsed samplePoints.
 * If existingSamplePointsState is provided,
 * it will search in the existingSamplePointsState and return the
 * updated controller instead.
 */
export const weatherStationSamplePointReducer = (
  accumulatedSamplePoints: SamplePoint[],
  currentWSSamplePoint: RawSamplePoint,
  existingSamplePointsState?: Record<SamplePointId, SamplePoint>
) => {
  // try to find the controller in the existingSamplePointsState if present
  if (existingSamplePointsState) {
    for (const controller of Object.values(existingSamplePointsState)) {
      if (isMatchingWeatherStationControllerAndSamplePoint(currentWSSamplePoint, controller)) {
        // Found the controller in Redux state, merge and add to accumulated sample points
        const updatedController = mergeWeatherStationSamplePoints(
          currentWSSamplePoint,
          controller as MergedWeatherStationSamplePoint
        );
        return [...accumulatedSamplePoints, updatedController];
      }
    }
  }

  // Try to merge with a controller in the accumulated sample points
  for (const accumulatedSP of accumulatedSamplePoints) {
    if (isMatchingWeatherStationControllerAndSamplePoint(currentWSSamplePoint, accumulatedSP)) {
      // Found a match in accumulated sample points, merge and return
      return accumulatedSamplePoints.map(sp =>
        sp.id === accumulatedSP.id
          ? mergeWeatherStationSamplePoints(currentWSSamplePoint, accumulatedSP)
          : sp
      );
    }
  }

  // If currentWSSamplePoint hasn't paired with any SP, it's likely a controller.
  // We know this because in `parseRawSamplePoints` we've sorted all controllers to be in the front, and also
  // our logic in `mergeWeatherStationSamplePoints` only accounts for merging child into controller,
  // not the other way around.
  // We parse it then add to the parsed sample points.
  if (currentWSSamplePoint.assetTypeId === RawAssetTypeCode.WEATHER_STATION_CONTROLLER) {
    const parsedWSSamplePoint = parseRawSamplePoint(currentWSSamplePoint);
    if (parsedWSSamplePoint) {
      return [...accumulatedSamplePoints, parsedWSSamplePoint];
    }
  }

  // No merging happened, return accumulated sample points unchanged
  return accumulatedSamplePoints;
};
